import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg';
}

const WIDTHS = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl' } as const;

export function Modal({ open, onClose, title, children, footer, width = 'md' }: ModalProps) {
  if (!open) return null;
  return (
    // Bottom sheet on mobile, centered dialog on >= sm.
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className={cn(
          'w-full bg-white shadow-xl overflow-hidden flex flex-col max-h-[92vh] rounded-t-2xl dark:bg-gray-900',
          'sm:rounded-2xl sm:max-h-[90vh]',
          WIDTHS[width],
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-100 shrink-0 dark:border-gray-800">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 -mr-1" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
        <div className="p-5 sm:p-6 overflow-y-auto">{children}</div>
        {footer && <div className="px-5 sm:px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-end gap-2 shrink-0 dark:border-gray-800 dark:bg-gray-800/30">{footer}</div>}
      </div>
    </div>
  );
}
